import { type PropsWithChildren, memo, useMemo } from "react";

import { type WithBreakpoints, getSizeWithBreakpoints } from "@react/utilities";
import { tw } from "classnames";

import type { ColorSwatchViewProps } from "./ColorSwatch.view";
import { ColorSwatchView } from "./ColorSwatch.view";

export type ColorSwatchSize = "tiny" | "mini" | "regular";

type Props = Omit<ColorSwatchViewProps, "sizeClass"> & {
  size?: ColorSwatchSize | WithBreakpoints<ColorSwatchSize>;
};

const styles = {
  sm: {
    tiny: tw("w-tiny-swatch", "h-tiny-swatch"),
    mini: tw("w-mini-swatch", "h-mini-swatch"),
    regular: tw("w-swatch", "h-swatch"),
  },
  md: {
    tiny: tw("md:w-tiny-swatch", "md:h-tiny-swatch"),
    mini: tw("md:w-mini-swatch", "md:h-mini-swatch"),
    regular: tw("md:w-swatch", "md:h-swatch"),
  },
  lg: {
    tiny: tw("lg:w-tiny-swatch", "lg:h-tiny-swatch"),
    mini: tw("lg:w-mini-swatch", "lg:h-mini-swatch"),
    regular: tw("lg:w-swatch", "lg:h-swatch"),
  },
};

const ColorSwatchBase = ({
  children,
  size: originalSize = "regular",
  ...rest
}: PropsWithChildren<Props>) => {
  const size = useMemo(() => {
    const resolvedSize =
      typeof originalSize === "string"
        ? { sm: originalSize, md: originalSize }
        : { ...originalSize };

    return getSizeWithBreakpoints(resolvedSize);
  }, [originalSize]);

  const sizeClass = useMemo(
    () =>
      tw(
        {
          [styles.sm.tiny]: size.sm === "tiny",
          [styles.sm.mini]: size.sm === "mini",
          [styles.sm.regular]: size.sm === "regular",
        },
        {
          [styles.md.tiny]: size.md === "tiny",
          [styles.md.mini]: size.md === "mini",
          [styles.md.regular]: size.md === "regular",
        },
        {
          [styles.lg.tiny]: size.lg === "tiny",
          [styles.lg.mini]: size.lg === "mini",
          [styles.lg.regular]: size.lg === "regular",
        }
      ),
    [size.sm, size.md, size.lg]
  );

  return (
    <ColorSwatchView sizeClass={sizeClass} {...rest}>
      {children}
    </ColorSwatchView>
  );
};

export const ColorSwatch = memo(ColorSwatchBase);
